import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { reducedMotion } from './playPrefs';

/**
 * The points chip — always visible, counts up on every earn.
 *
 * The program name is a SETTING (`programName`); when a grocer has not agreed
 * to the figures yet, `sample` is true and the chip says so in plain words. We
 * never show a real-looking balance that did not come from the grocer.
 */
export const PointsChip = ({ points = 0, programName, sample = true, testId = 'play-points-chip' }) => {
    const { t } = useTranslation();
    const [shown, setShown] = useState(points);
    const [bump, setBump] = useState(false);
    const prev = useRef(points);

    useEffect(() => {
        if (points === prev.current) return undefined;
        const from = prev.current;
        prev.current = points;
        setBump(true);
        if (reducedMotion()) {
            setShown(points);
            const calm = setTimeout(() => setBump(false), 400);
            return () => clearTimeout(calm);
        }
        const steps = Math.min(Math.abs(points - from), 18) || 1;
        let i = 0;
        const id = setInterval(() => {
            i += 1;
            setShown(Math.round(from + ((points - from) * i) / steps));
            if (i >= steps) clearInterval(id);
        }, 45);
        const done = setTimeout(() => setBump(false), 700);
        return () => { clearInterval(id); clearTimeout(done); };
    }, [points]);

    return (
        <div className={`inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 dark:border-emerald-500/30 dark:bg-emerald-500/10 ${bump ? 'play-bump' : ''}`}
            data-testid={testId}>
            <Sparkles className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <span className="text-sm font-bold text-emerald-800 dark:text-emerald-300" data-testid={`${testId}-value`}>
                {shown}
            </span>
            <span className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">
                {programName || t('play.points')}
            </span>
            {sample ? (
                <span className="rounded-full bg-[#13265C] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white dark:bg-white dark:text-[#13265C]"
                    data-testid={`${testId}-sample`}>
                    {t('play.sample')}
                </span>
            ) : null}
        </div>
    );
};

export default PointsChip;
